import { useState, useEffect, useCallback } from 'react';
import { getItem, setItem } from './utils/scopedSettingsStorage';

type Translations = Record<string, Record<string, string>>;

export const translations: Translations = {
  English: {
    // The turn step stream. The audit timeline has always been translated;
    // this surface was hardcoded Chinese, so in English the two named the same
    // stage of the same turn in different languages.
    'Step plan generation': 'Plan Generation',
    // Named for what decides, not for what it does: this is the built-in
    // checker, and CODESYS's own compiler further down can and does disagree
    // with it. Both used to be called "validation", which left no way to tell
    // which answer the turn was reporting.
    'Step compile validation': 'Preflight (built-in rules)',
    'Step repair issues': 'Repair Issues',
    'Native compile': 'Native compile',
    "Not compiled in this turn's run": "Not compiled in this turn's run",
    'Generation steps': 'Generation steps',
    'Step thinking': 'Thinking',
    'Step continuing': 'Continuing',
    'Identified {items}.': 'Identified {items}.',
    '{count} I/O points': '{count} I/O points',
    '{count} devices': '{count} devices',
    '{count} safety constraints': '{count} safety constraints',
    '{count} key conditions': '{count} key conditions',
    'devices: {items}': 'devices: {items}',
    'I/O: {items}': 'I/O: {items}',
    'safety: {items}': 'safety: {items}',
    'conditions: {items}': 'conditions: {items}',
    '; ': '; ',
    'Still open: {items} — assumed conservatively in the cards below.':
      'Still open: {items} — assumed conservatively in the cards below.',
    // The same, on a turn with no cards to point at.
    'Still open: {items} — assumed conservatively.': 'Still open: {items} — assumed conservatively.',
    // Keyed by its own English text so a lost entry still separates the list.
    ', ': ', ',
    '{count} items confirmed.': '{count} items confirmed.',
    'Split into {count} subtasks.': 'Split into {count} subtasks.',
    'Plan needs no manual approval.': 'Plan needs no manual approval.',
    'Plan approved (confirmed by user).': 'Plan approved (confirmed by user).',
    'Plan approved automatically.': 'Plan approved automatically.',
    'Plan approved after changes.': 'Plan approved after changes.',
    'Plan rejected.': 'Plan rejected.',
    'Re-planning subtasks.': 'Re-planning subtasks.',
    'Waiting for plan confirmation.': 'Waiting for plan confirmation.',
    'Approval window closed; the plan is still yours to run: press Continue Execution.': 'Approval window closed; the plan is still yours to run: press Continue Execution.',
    'Generated {count} lines of code.': 'Generated {count} lines of code.',
    ' (retry {count})': ' (retry {count})',
    'No usable program produced; validation stopped.': 'No usable program produced; validation stopped.',
    'Validation passed.': 'Validation passed.',
    'Review required': 'Review required',
    'New Chat': 'New Chat',
    'New Project': 'New Project',
    'Projects': 'Projects',
    'Recent Projects': 'Recent Projects',
    'No recent projects': 'No recent projects',
    'Back': 'Back',
    'Create': 'Create',
    'Project name placeholder': 'Project name',
    'Delete Project': 'Delete Project',
    'Rename Project': 'Rename Project',
    'Project Title': 'Project Title',
    'Save Rename': 'Save Rename',
    'Settings': 'Settings',
    'AppTitle': 'RealPLC - AI Defines the Future of PLC Programming',
    'SidebarTitle': 'RealPLC',
    'Welcome to PLC Logic Forge': 'Welcome to RealPLC AI Forge',
    'Select a sample prompt or describe your automation requirement below.': 'Select a sample prompt or describe your automation requirement below.',
    'Target Platform': 'Target Platform',
    'Programming Language': 'Programming Language',
    'Software Version': 'Software Version',
    'Hardware Series': 'Hardware Series',
    'PLC Model': 'PLC Model',
    'Firmware Version': 'Firmware Version',
    'e.g. TIA Portal v18': 'e.g. TIA Portal v18',
    'e.g. S7-1200': 'e.g. S7-1200',
    'e.g. S7-1215C DC/DC/DC': 'e.g. S7-1215C DC/DC/DC',
    'e.g. V4.5': 'e.g. V4.5',
    'Requirement Thinking': 'Requirement Thinking',
    'Requirement Analysis': 'Requirement Analysis',
    'Requirement Analysis (updated with confirmations)': 'Requirement Analysis (updated with confirmations)',
    'Plan Generation': 'Plan Generation',
    'Execution Plan': 'Execution Plan',
    'Plan Basis': 'Plan Basis',
    'Plan Basis / Engineering Constraints': 'Plan Basis / Engineering Constraints',
    'Planning in progress...': 'Generating plan...',
    'Reasoning Process': 'Reasoning Process',
    'Requirement analysis in progress....': 'Analyzing requirement...',
    'Explanation': 'Explanation',
    'Logic Flowchart': 'Logic Flowchart',
    'Project Summary': 'Project Summary',
    'Summary': 'Summary',
    'Describe your automation requirement...': 'Describe your automation requirement...',
    'Send': 'Send',
    'Classification': 'Classification',
    'Planning': 'Planning',
    'Planning Process': 'Planning Process',
    'Engineering Reasoning': 'Plan Basis',
    'Error Analysis': 'Error Analysis',
    'Show': 'Show',
    'Hide': 'Hide',
    'Generated Logic': 'Generated Logic',
    'Usage Example': 'Usage Example',
    'Source Code': 'Source Code',
    'Copy Code': 'Copy Code',
    'Copy Project Summary': 'Copy Project Summary',
    'Copy Summary': 'Copy Summary',
    'Download Code': 'Download Code',
    'Program File': 'Program File',
    'Ready to Download': 'Ready to Download',
    'Final PLC program file': 'Final PLC program file',
    'Download Program': 'Download Program',
    'Static Analysis Warnings': 'Static Analysis Warnings',
    'Line': 'Line',
    'AI Provider Settings': 'AI Provider Settings',
    'Active Provider': 'Active Provider',
    'Model': 'Model',
    'API Keys': 'API Keys',
    'Base URLs (Optional)': 'Base URLs (Optional)',
    'Custom Model': 'Custom Model',
    'Custom Provider': 'Custom Provider',
    'Custom model connections': 'Custom model connections',
    'Custom provider connections': 'Custom provider connections',
    'Models in this provider': 'Models in this provider',
    'New Model': 'New Model',
    'No models added yet.': 'No models added yet.',
    'Add Model': 'Add Model',
    'Add a custom provider before saving.': 'Add a custom provider before saving.',
    'Optional': 'Optional',
    'Fetch Models': 'Fetch Models',
    'Fetching models...': 'Fetching models...',
    'No new models found.': 'No new models found.',
    'Model discovery failed': 'Model discovery failed',
    'Discovered Models': 'Discovered Models',
    'Add All': 'Add All',
    'Enable': 'Enable',
    'Disable': 'Disable',
    'Enter a Base URL before fetching models.': 'Enter a Base URL before fetching models.',
    'gpt-4o-mini, deepseek-chat, qwen-plus': 'gpt-4o-mini, deepseek-chat, qwen-plus',
    'Add': 'Add',
    'Remove': 'Remove',
    'Model Name': 'Model Name',
    'Model Provider Name': 'Model Provider Name',
    'Model Name (OpenAI format)': 'Model Name (OpenAI format)',
    'OpenAI-compatible provider': 'OpenAI-compatible provider',
    'Use the exact model name accepted by the provider API.': 'Use the exact model name accepted by the provider API.',
    'Base URL': 'Base URL',
    'Test Connection': 'Test Connection',
    'Testing connection...': 'Testing connection...',
    'Connection succeeded': 'Connection succeeded',
    'Connection failed': 'Connection failed',
    'Enter a model name before saving.': 'Enter a model name before saving.',
    'Enter a Base URL before saving.': 'Enter a Base URL before saving.',
    'Enter a model name before testing.': 'Enter a model name before testing.',
    'Enter a Base URL before testing.': 'Enter a Base URL before testing.',
    'Custom instructions enabled': 'Custom instructions enabled',
    'Using default instructions': 'Using default instructions',
    'Preferences': 'Preferences',
    'Theme': 'Theme',
    'Default Language': 'Default Language',
    'Save Changes': 'Save Changes',
    'System': 'System',
    'Dark': 'Dark',
    'Light': 'Light',
    'Chinese': 'Chinese',
    'English': 'English',
    'Motion': 'Motion',
    'PID': 'PID',
    'Sequencing': 'Sequencing',
    'Motor': 'Motor',
    'Process': 'Process',
    'SafetyCategory': 'Safety',
    'Safety': 'Safety',
    'Data': 'Data',
    'HVAC': 'HVAC',
    'Template': 'Template',
    'Conveyor Control': 'Conveyor Control',
    'PID Loop': 'PID Loop',
    'Traffic Sequence': 'Traffic Sequence',
    'Motor Starter': 'Motor Starter',
    "Control a conveyor belt with start/stop buttons, a safety e-stop, and a 10s timeout if the sensor isn't triggered.": "Control a conveyor belt with start/stop buttons, a safety e-stop, and a 10s timeout if the sensor isn't triggered.",
    "Create a PID temperature control loop for a heating element with anti-windup.": "Create a PID temperature control loop for a heating element with anti-windup.",
    "Implement a traffic light sequence for a 4-way intersection using timers and states.": "Implement a traffic light sequence for a 4-way intersection using timers and states.",
    "Write a function block to handle a motor starter with overload protection and feedback monitoring.": "Write a function block to handle a motor starter with overload protection and feedback monitoring.",
    'AI 定义 PLC 编程的未来': 'AI 定义 PLC 编程的未来',
    'AI can make mistakes. Please verify generated logic before deploying to production hardware.': 'AI can make mistakes. Please verify generated logic before deploying to production hardware.',
    'Expert Collection': 'Expert Collection',
    'More': 'More',
    'Search Web': 'Search Web',
    'Upload File': 'Upload File',
    'Drop files to upload': 'Drop files to upload',
    'Files': 'Files',
    'Uploading & parsing...': 'Uploading & parsing...',
    'Maximum {n} files allowed': 'Maximum {n} files allowed',
    'Please wait for file processing to finish.': 'Please wait for file processing to finish.',
    'A file could not be read. Remove it to send.': 'A file could not be read. Remove it to send.',
    'Failed to refresh file status': 'Failed to refresh file status',
    'Failed to load image preview': 'Failed to load image preview',
    'Failed to delete uploaded file': 'Failed to delete uploaded file',
    'Please analyze the uploaded files and provide suggestions within the RealPLC context.': 'Please analyze the uploaded files and provide suggestions within the RealPLC context.',
    'End Conversation': 'End Conversation',
    'Tank Control': 'Tank Control',
    'Batch Process': 'Batch Process',
    'Alarm Manager': 'Alarm Manager',
    'Data Logger': 'Data Logger',
    'Packaging Line': 'Packaging Line',
    'HVAC Control': 'HVAC Control',
    'Control a liquid tank with inlet/outlet valves, level sensors, and start/stop buttons.': 'Control a liquid tank with inlet/outlet valves, level sensors, and start/stop buttons.',
    'Control a multi-step batch recipe process including ingredient dosing, mixing, and discharging.': 'Control a multi-step batch recipe process including ingredient dosing, mixing, and discharging.',
    'Implement an alarm handler with priority levels, acknowledgment, auto-reset, and horn control.': 'Implement an alarm handler with priority levels, acknowledgment, auto-reset, and horn control.',
    'Create a circular data logger to record analog values and timestamps into a buffer.': 'Create a circular data logger to record analog values and timestamps into a buffer.',
    'Automate a packaging line including filling, sealing, labeling, and rejection functions.': 'Automate a packaging line including filling, sealing, labeling, and rejection functions.',
    'Control a heating/cooling HVAC system with zone temperatures, fan speeds, and scheduling.': 'Control a heating/cooling HVAC system with zone temperatures, fan speeds, and scheduling.',
    'Less': 'Less',
    'Export': 'Export',
    'Select Format': 'Select Format',
    'PNG Image': 'PNG Image',
    'JPG Image': 'JPG Image',
    'SVG Vector': 'SVG Vector',
    'Logic Flow Visualization': 'Logic Flow Visualization',
    'Fullscreen View': 'Fullscreen View',
    'User Profile': 'User Profile',
    'Account and Profile': 'Account and Profile',
    'Personal display': 'Personal display',
    'Used for your workspace avatar and sidebar identity.': 'Used for your workspace avatar and sidebar identity.',
    'Used for your avatar, sidebar, and collaboration identity.': 'Used for your avatar, sidebar, and collaboration identity.',
    'Account security': 'Account security',
    Account: 'Account',
    'Authenticated User': 'Authenticated User',
    'Signed in as': 'Signed in as',
    'Use account security to update your username, profile, or password.': 'Use account security to update your username, profile, or password.',
    'Manage account details': 'Manage account details',
    'Change password': 'Change password',
    'Account Center': 'Account Center',
    'Account Profile': 'Account Profile',
    'Account Security': 'Account Security',
    'Current sign-in': 'Current sign-in',
    'Login Account': 'Login Account',
    'Personal Profile': 'Personal Profile',
    'Password and sign-in': 'Password and sign-in',
    'Not set': 'Not set',
    'Loading account profile': 'Loading account profile',
    'Failed to load account profile': 'Failed to load account profile',
    'Failed to update account profile': 'Failed to update account profile',
    'Account profile updated': 'Account profile updated',
    'Username': 'Username',
    'Role': 'Role',
    'First Name': 'First Name',
    'Last Name': 'Last Name',
    'Save Account Profile': 'Save Account Profile',
    'Current Password': 'Current Password',
    'New Password': 'New Password',
    'Confirm New Password': 'Confirm New Password',
    'Update Password': 'Update Password',
    'Failed to update password': 'Failed to update password',
    'Password updated. Please sign in with the new password next time.': 'Password updated. Please sign in with the new password next time.',
    'Use at least 8 characters. Your current password is verified before RealPLC updates Keycloak.': 'Use at least 8 characters. Your current password is verified before updating the account password.',
    'Use at least 8 characters. Your current password is verified before updating the account password.': 'Use at least 8 characters. Your current password is verified before updating the account password.',
    'Viewer': 'Viewer',
    'Administrators can reset passwords, but cannot view passwords after users change them.': 'Administrators can reset passwords, but cannot view passwords after users change them.',
    'Sign out now': 'Sign out now',
    'Latest': 'Latest',
    'Configure engineering context': 'Configure engineering context',
    'Collapse sidebar': 'Collapse sidebar',
    'Open sidebar': 'Open sidebar',
    'Project actions': 'Project actions',
    'Confirm rename': 'Confirm rename',
    'Restored your unsent draft': 'Restored your unsent draft',
    'Clear': 'Clear',
    'Automatic Repair Completed': 'Automatic Repair Completed',
    'Automatic Repair Completed With Warnings': 'Automatic Repair Completed With Warnings',
    'Automatic Repair Finished With Issues': 'Automatic Repair Finished With Issues',
    'Automatic Repair Needs Review': 'Automatic Repair Needs Review',
    'Automatic Repair In Progress': 'Automatic Repair In Progress',
    'Automatic Repair Skipped': 'Automatic Repair Skipped',
    'Repair Attempts': 'Repair Attempts',
    'Attempt': 'Attempt',
    'Final Validation': 'Final Validation',
    'Repair': 'Repair',
    'Total': 'Total',
    'Validation': 'Validation',
    'The main code block has been updated with the repaired code.': 'The main code block has been updated with the repaired code.',
    'Auto Repaired': 'Auto Repaired',
    'Auto Repairing': 'Auto Repairing',
    'Name': 'Name',
    'Display Name': 'Nickname',
    'Display name placeholder': 'Enter a nickname',
    'Username is required': 'Username is required.',
    'Username cannot contain Chinese characters': 'Username cannot contain Chinese characters.',
    'Username rule hint': 'Use 3-255 characters: letters, numbers, dot, underscore, @, plus, or hyphen.',
    'Phone rule hint': 'Phone number can only include digits, spaces, +, -, or parentheses.',
    'Phone': 'Phone',
    'Your name': 'Your name',
    'Today': 'Today',
    'Yesterday': 'Yesterday',
    '7 Days': '7 Days',
    '30 Days': '30 Days',
    'Starred': 'Starred',
    'Star': 'Star',
    'Unstar': 'Unstar',
    'Rename': 'Rename',
    'Delete': 'Delete',
    'Help': 'Help',
    'Contact': 'Contact',
    'Sponsor': 'Sponsor Us',
    'Sponsor Us': 'Sponsor Us',
    'Thinking...': 'Thinking...',
    'Thinking in progress': 'Thinking...',
    'Thinking completed': 'Thinking completed',
    'Thinking summary in progress': 'Understanding the current input, context, and available project material.',
    'Thinking summary analysis ready': 'Requirement understanding is ready.',
    'Thinking summary clarification ready': 'Requirement understanding is ready; key confirmation items are prepared below.',
    'Thinking summary plan ready': 'Requirement understanding is complete and the engineering plan is ready.',
    'Requirement handoff after thinking': "I've understood your request. Next I'll organize the key signals, control logic, and confirmation points.",
    'Plan generation ready transition': 'Requirement confirmed. Generating the engineering plan now.',
    'Thinking summary non engineering': 'This turn is not a PLC engineering task, so requirement analysis is skipped.',
    'Thinking progress requirement analysis': 'Requirement analysis is streaming; key control objects, I/O, safety constraints, and uncertainties are being organized.',
    'Thinking progress waiting confirmations': 'Confirmation items are ready. Waiting for required manual choices before engineering reasoning and plan generation.',
    'Thinking progress confirmations ready': 'Required confirmations are ready. The next step is engineering reasoning.',
    'Thinking progress streaming': 'Analyzing the request and preparing the engineering workflow...',
    'Thinking step intent': 'Identify this turn’s intent',
    'Thinking step context': 'Check whether it continues the previous requirement',
    'Thinking step sources': 'Read current input, context, and attachments',
    'Thinking step engineering route': 'Decide whether to enter the PLC engineering flow',
    'Thinking step output shape': 'Decide whether this needs PRG, FB, FC, or a normal reply',
    'Thinking step io safety': 'Identify control objects, I/O, interlocks, and fault handling',
    'Thinking step confirmations': 'Select only confirmation items that affect code behavior',
    'Thinking step non engineering': 'Classified as a non-PLC engineering turn',
    'Requirement analysis transition clarify': 'Only the items that affect control logic, code structure, or safety behavior are confirmed below.',
    'Reasoning': 'Reasoning',
    'Generating code...': 'Generating code...',
    'Running validation...': 'Running validation...',
    'Attempting auto-repair...': 'Attempting auto-repair...',
    'Finalizing...': 'Finalizing...',
    'Classifying request...': 'Classifying request...',
    'Planning...': 'Planning...',
    'Analyzing requirements...': 'Analyzing requirements...',

    'Decomposing tasks...': 'Decomposing tasks...',
    'Assessing risks...': 'Assessing risks...',
    'Self-checking plan quality...': 'Self-checking plan quality...',
    'Plan completed': 'Plan completed',
    'Plan generation did not produce results': 'Plan generation did not produce results',
    'Plan detail table': 'Plan detail table',
    'Plan detail table hint': 'Wide planning tables are collapsed to keep the requirement flow readable.',
    'subtasks': 'subtasks',
    'Subtask Count ({count})': 'Subtasks ({count})',
    'Plan Review': 'Plan Approval',
    'Requirement Confirmation': 'Requirement Confirmation',
    'Awaiting Confirmation': 'Awaiting Confirmation',
    'Validating': 'Validating',
    'Plan timed out, click to continue execution': 'Approval window closed — the plan is still yours to run',
    'Continue Execution': 'Continue Execution',
    'Approve Plan': 'Approve Plan',
    'Reject Plan': 'Reject Plan',
    'Modify Plan': 'Modify Plan',
    'Plan approved': 'Plan approved',
    'Plan rejected, re-planning...': 'Plan rejected, re-planning...',
    'Re-planning': 'Re-planning',
    'Subtask Re-planning': 'Subtask Re-planning',
    'Cached Plan Review': 'Cached Plan Review',
    'Re-planning in progress....': 'Re-planning in progress....',
    'Plan review timeout, auto-approved': 'Plan review timed out; manual approval is required',
    'Auto-approved': 'Auto-approved',
    'Manually approved': 'Manually approved',
    'Auto-approving in {seconds}s': 'Auto-approving in {seconds}s',
    'Pause auto-approval': 'Pause auto-approval',
    'Resume auto-approval': 'Resume auto-approval',
    'Plan auto-approval': 'Plan auto-approval',
    'Countdown seconds': 'Countdown seconds',
    'Enabled': 'Enabled',
    'Disabled': 'Disabled',
    'Architecture': 'Architecture',
    'Variable Strategy': 'Variable Strategy',
    'Safety Approach': 'Safety Approach',
    'Risks': 'Risks',
    'Coverage': 'Coverage',
    'Safety Coverage': 'Safety Coverage',
    'Self Check': 'Self Check',
    'Dependencies': 'Dependencies',
    'Dependencies (comma-separated)': 'Dependencies (comma-separated)',
    'Acceptance Criteria': 'Acceptance Criteria',
    'Complexity': 'Complexity',
    'Title': 'Title',
    'Description': 'Description',
    'complexity.simple': 'simple',
    'complexity.moderate': 'moderate',
    'complexity.complex': 'complex',
    'Logic Flow': 'Logic Flow',
    'Edit in draw.io': 'Edit in draw.io',
    'Opening draw.io...': 'Opening draw.io...',
    'draw.io editor ready': 'draw.io editor ready',
    'draw.io diagram saved': 'draw.io diagram saved',
    'draw.io diagram autosaved': 'draw.io diagram autosaved',
    'Download draw.io XML': 'Download draw.io XML',
    'Download Mermaid Source': 'Download Mermaid Source',
    'Download .drawio': 'Download .drawio',
    'Download Mermaid': 'Download Mermaid',
    'Code': 'Code',
    'Diagram': 'Diagram',
    'Show Raw Code': 'Show Raw Code',
    'Show Diagram': 'Show Diagram',
    'Zoom Out': 'Zoom Out',
    'Reset Zoom': 'Reset Zoom',
    'Zoom In': 'Zoom In',
    'Fullscreen': 'Fullscreen',
    'Chart auto-corrected ({n} changes)': 'Chart auto-corrected ({n} changes)',
    'Chart degraded ({n} changes)': 'Chart degraded ({n} changes)',
    'View Details': 'View Details',
    'Hide Details': 'Hide Details',
    'Corrections': 'Corrections',
    'Final error': 'Final error',
    'Strict Safety Check': 'Strict Safety Check',
    'Safety Verification Report': 'Safety Verification Report',
    'Raw Code': 'Raw Code',
    'Framework Validation': 'Framework Validation',
    'Quality Check': 'Quality Check',
    'Issues': 'issues',
    'No issues': 'No issues',
    'Risk': 'Risk',
    'After repair': 'After repair',
    'Issue': 'Issue',
    'low': 'low',
    'medium': 'medium',
    'high': 'high',
    'critical': 'critical',
    'Repair History': 'Repair History',
    'Structured Requirement': 'Structured Requirement',
    'Generated Test Cases': 'Generated Test Cases',
    'Task Type': 'Task Type',
    'Strategy': 'Strategy',
    'Scenario Template': 'Scenario Template',
    'Modes': 'Modes',
    'IO Points': 'IO Points',
    'Signal Name': 'Signal Name',
    'Address': 'Address',
    'Variable Name': 'Variable Name',

    'Key Conditions': 'Key Conditions',
    'Missing Information': 'Missing Information',
    'Preconditions': 'Preconditions',
    'Steps': 'Steps',
    'Expected': 'Expected',
    'Syntax': 'Syntax',
    'Semantic': 'Semantic',
    'Vendor': 'Vendor',
    'Test': 'Test',
    'Pass': 'Pass',
    'Check': 'Check',
    'Warning': 'Warning',
    'Failed': 'Failed',
    'Skipped': 'Skipped',
    'Degraded': 'Degraded',
     'Template Rule': 'Template Rule',
     'DocumentTitle': 'RealPLC - AI Defines the Future of PLC Programming',
    'Landing.NavCapabilities': 'Capabilities',
    'Landing.NavSafety': 'Safety',
    'Landing.NavPlatforms': 'Platforms',
    'Landing.EnterWorkspace': 'Enter Workspace',
    'Landing.Badge': 'Industrial AI for IEC 61131-3 workflows',
    'Landing.Title': 'Industrial AI for Real PLC Engineering',
    'Landing.Subtitle': 'Generate safety-aware IEC 61131-3 control logic for real automation workflows across Siemens, CODESYS, Inovance, and more.',
    'Landing.Start': 'Start with RealPLC',
    'Landing.ViewCapabilities': 'View Capabilities',
    'Landing.RuntimeTitle': 'Control Logic Runtime',
    'Landing.RuntimeSubtitle': 'Structured generation pipeline',
    'Landing.Online': 'Online',
    'Landing.Requirement': 'Requirement',
    'Landing.RequirementLine1': 'Create a safety-aware motor sequence for an Inovance PLC.',
    'Landing.RequirementLine2': 'Include overload trip, run feedback timeout, and operator reset flow.',
    'Landing.Output': 'Output',
    'Landing.Target': 'Target',
    'Landing.SafetyLayer': 'Safety Layer',
    'Landing.SafetyLayerValue': 'Strict validation enabled',
    'Landing.Routing': 'Routing',
    'Landing.RoutingValue': 'Structured output pipeline',
    'Landing.BuiltFor': 'Built for production logic',
    'Landing.FlowTitle': 'From intent to control logic',
    'Landing.FlowSubtitle': 'Move from a plain-language requirement into structured reasoning, platform-aware output, and a workspace that is ready for review.',
    'Landing.Step01': 'Requirement',
    'Landing.Step01Desc': 'Capture the control objective, operating mode, and field constraints.',
    'Landing.Step02': 'Structured reasoning',
    'Landing.Step02Desc': 'Apply safety logic, sequencing rules, and vendor-aware engineering context.',
    'Landing.Step03': 'PLC output',
    'Landing.Step03Desc': 'Review generated logic, inspect structure, and continue in the workspace.',
    'Landing.PlatformsTitle': 'Designed for industrial workflows',
    'Landing.Ready': 'Ready',
    'Landing.CtaTitle': 'Start building real control logic',
    'Landing.CtaBody': 'Structured generation for industrial automation, with a cleaner path into your current workspace.',
    'Landing.Footer': '2026 RealPLC Project. Structured generation for industrial automation.',
    'Landing.IndustrialWorkspace': 'Industrial AI Workspace',
    'Landing.TagNative': 'IEC 61131-3 Native',
    'Landing.TagSafety': 'Safety-Aware',
    'Landing.TagStructured': 'Structured Output',
    'Landing.TagMultiPlatform': 'Multi-Platform Ready',
    'Landing.Capability1Title': 'Safety Comes First',
    'Landing.Capability1Body': 'Interlocks, stop conditions, and structured validation are built into the generation flow.',
    'Landing.Capability2Title': 'Native Engineering Output',
    'Landing.Capability2Body': 'Produce IEC 61131-3 logic shaped for practical PLC workflows, not generic code samples.',
    'Landing.Capability3Title': 'Platform-Aware',
    'Landing.Capability3Body': 'Engineered for Siemens, Inovance, CODESYS, and multi-platform PLC deployment.',
    'Execute Plan': 'Execute Plan',
    'Subtasks': 'Subtasks',
    'Share of the analysed safety constraints that a safety subtask covers. It does not judge whether the safety logic is right.':
      'Share of the analysed safety constraints that a safety subtask covers. It does not judge whether the safety logic is right.',
    'Blocking': 'Blocking',
    'Advisory': 'Advisory',
    'I have read the {count} blocking self-check finding(s) above and still want to execute the plan':
      'I have read the {count} blocking self-check finding(s) above and still want to execute the plan',
    'Edit': 'Edit',
    'Feedback': 'Feedback',
    'Skip': 'Skip',
    'Re-plan': 'Re-plan',
    'Submit Changes': 'Submit Changes',
    'Submitted': 'Submitted',
    'Required': 'Required',
    'Auto-confirmed': 'auto-confirmed',
    'Describe plan issues, AI will re-plan...': 'Describe plan issues, AI will re-plan...',
    'Regenerate subtask': 'Regenerate subtask',
    'Optional constraints...': 'Optional constraints...',
    'Plan review submit failed': 'Plan review submit failed',
    'Plan review request id is missing, please retry.': 'Plan review request id is missing, please retry.',
    'Details': 'Details',
    'clarify.other': 'Other...',
    'clarify.pleaseSelect': 'Please select',
    'clarify.confirmAndSend': 'Confirm & Send',
    'clarify.promptHint': 'or type your reply directly',
    'clarify.confirmTitle': 'Please confirm the following',
    'clarify.responseIntro': 'Regarding the previous clarification questions:',
    'clarify.notSelected': '(Not selected)',
    'clarify.customValue': '(Custom)',
    'clarify.customPlaceholder': 'Enter custom option...',
    'clarify.separator': ', ',
    'clarify.defaultValue': 'Suggested default',
    'clarify.multiSelect': 'choose any',
    'clarify.review.safety': 'safety decision',
    'clarify.review.keyDecision': 'key decision',
    'clarify.review.noDefault': 'needs an answer',
    'clarify.collapsedGroup': '{count} more already have a recommendation',
    'clarify.blockedByUnanswered': '{count} still need an answer',
    'clarify.subject.estop': 'Emergency stop',
    'clarify.subject.timeout': 'Timeout protection',
    'clarify.subjectCount': '{count} questions',
    'clarify.statedInRequirement': 'from your request',
    'clarify.autoSubmitCountdown': 'All options auto-confirmed. Submitting in {seconds}s...',
    'Execution': 'Execution',
    'Running': 'Running',
    'Needs review': 'Needs review',
    'Needs input': 'Needs input',
    'Needs approval': 'Needs approval',
    'Passed': 'Passed',
    'Repaired': 'Repaired',
    'Pending': 'Pending',
    'Workspace': 'Workspace',
    'Engineering Workspace': 'Engineering Workspace',
    'artifacts': 'artifacts',
    'context active': 'context active',
    'Expand': 'Expand',
    'Context': 'Context',
    'Requirement': 'Requirement',
    'IO': 'IO',
    'Plan': 'Plan',
    'View details': 'View details',
    'Show less': 'Show less',
    'Scanning...': 'Scanning...',
    'Converting...': 'Converting...',
    'Parsing...': 'Parsing...',
    'Ready': 'Ready',
    'Parsed (compat)': 'Parsed (compat)',
    'Text only (compat)': 'Text only (compat)',
    'Parse failed': 'Parse failed',
    'Conversion failed': 'Conversion failed',
    'File exceeds {n}MB': 'File exceeds {n}MB limit',
    'Unsupported type': 'Unsupported type',
    'Only {n} files allowed': 'Only {n} files allowed',
    'Processing...': 'Processing...',
    'Requirement IO': 'Requirement IO',
    'Code IO': 'Code IO',
    'I/O Points': 'I/O Points',
    'Signals in Code': 'Signals in Code',
    'IO count mismatch': 'IO count mismatch',
    'The generated code may use a different number of signals than specified.': 'The generated code may use a different number of signals than specified.',
  },
  Chinese: {
    // The turn step stream. The audit timeline has always been translated;
    // this surface was hardcoded Chinese, so in English the two named the same
    // stage of the same turn in different languages.
    // 模型选择器里那家 provider 平台没配 key、用户也没填自己的 key。
    'No key configured': '未配置密钥',
    'Hidden': '隐藏',
    // 计划审批的分数：原来叫「覆盖率」，会被读成「需求被正确实现的比例」。
    'Signal names': '信号名命中',
    'Share of analysed signal names that appear in a subtask. It does not check values, modes or architecture.':
      '分析列出的信号名中，在子任务里出现过的比例。它不校验取值、模式或架构是否兑现。',
    'Step plan generation': '方案规划',
    'Step compile validation': '预检（内置规则）',
    'Step repair issues': '修复问题',
    // 编译器这一步是后加的，字典里一直没有它，中文回合里于是显示英文查找键。
    // 不带厂商名（用户 2026-09-23）：Siemens 回合的这一行是 TIA 的编译。
    'Native compile': '原生编译',
    // 只说本轮流程没编译：页面上自己发起的编译不写回消息（评审，2026-09-23）。
    "Not compiled in this turn's run": '本轮流程未编译',
    'Generation steps': '生成过程',
    'Step thinking': '思考中',
    'Step continuing': '正在继续',
    'Identified {items}.': '已识别 {items}。',
    '{count} I/O points': '{count} 个 I/O 点',
    '{count} devices': '{count} 个设备',
    '{count} safety constraints': '{count} 项安全约束',
    '{count} key conditions': '{count} 项关键条件',
    'devices: {items}': '设备：{items}',
    'I/O: {items}': 'I/O：{items}',
    'safety: {items}': '安全约束：{items}',
    'conditions: {items}': '关键条件：{items}',
    '; ': '；',
    'Still open: {items} — assumed conservatively in the cards below.':
      '尚未给出：{items}，已按保守默认假设，可在下方确认中调整。',
    'Still open: {items} — assumed conservatively.': '尚未给出：{items}，已按保守默认假设。',
    ', ': '、',
    '{count} items confirmed.': '{count} 项已确认。',
    'Split into {count} subtasks.': '拆成 {count} 个实施项。',
    'Plan needs no manual approval.': '计划无需人工审批。',
    'Plan approved (confirmed by user).': '计划已通过（用户确认）。',
    'Plan approved automatically.': '计划已自动批准。',
    'Plan approved after changes.': '计划已修改后通过。',
    'Plan rejected.': '计划被拒绝。',
    'Re-planning subtasks.': '正在重新规划子任务。',
    'Waiting for plan confirmation.': '等待计划确认。',
    'Approval window closed; the plan is still yours to run: press Continue Execution.': '审批时间已过，计划仍可执行：点「继续执行」。',
    'Generated {count} lines of code.': '生成 {count} 行程序。',
    ' (retry {count})': '（第 {count} 次重试）',
    'No usable program produced; validation stopped.': '未产出可用程序，已停止校验。',
    'Validation passed.': '校验通过。',
    'Review required': '需复核',
    'New Chat': '新建对话',
    'New Project': '新建项目',
    'Projects': '项目',
    'Recent Projects': '最近项目',
    'No recent projects': '暂无最近项目',
    'Back': '返回',
    'Create': '创建',
    'Project name placeholder': '项目名称',
    'Delete Project': '删除项目',
    'Rename Project': '重命名项目',
    'Project Title': '项目标题',
    'Save Rename': '保存重命名',
    'Settings': '设置',
    'Sponsor Us': '支持我们',
    'AppTitle': 'RealPLC - AI 定义 PLC 编程的未来',
    'SidebarTitle': 'RealPLC',
    'Welcome to PLC Logic Forge': '欢迎使用 RealPLC AI Forge',
    'Select a sample prompt or describe your automation requirement below.': '选择一个示例提示或在下方描述您的自动化需求。',
    'Target Platform': '目标平台',
    'Programming Language': '编程语言',
    'Software Version': '软件版本',
    'Hardware Series': '硬件系列',
    'PLC Model': 'PLC型号',
    'Firmware Version': '固件版本',
    'e.g. TIA Portal v18': '例如 TIA Portal v18',
    'e.g. S7-1200': '例如 S7-1200',
    'e.g. S7-1215C DC/DC/DC': '例如 S7-1215C DC/DC/DC',
    'e.g. V4.5': '例如 V4.5',
    'Requirement Analysis': '需求分析',
    'Requirement Analysis (updated with confirmations)': '需求分析（已按确认更新）',
    'Requirement Thinking': '需求分析',
    'Plan Generation': '生成计划',
    'Execution Plan': '执行计划',
    'Plan Basis': '计划依据',
    'Plan Basis / Engineering Constraints': '计划依据 / 工程约束',
    'Planning in progress...': '正在生成计划...',
    'Plan detail table': '计划明细表格',
    'Plan detail table hint': '宽表格已默认折叠，避免打断需求确认流程；需要时展开查看完整明细。',
    'Reasoning Process': '推理过程',
    'Requirement analysis in progress....': '正在分析需求...',
    'Explanation': '代码解释',
    'Logic Flowchart': '逻辑流程图',
    'Project Summary': '项目总结',
    'Summary': '总结',
    'Describe your automation requirement...': '描述你的自动化需求...',
    'Send': '发送',
    'Classification': '需求分类',
    'Planning': '工程规划',
    'Planning Process': '规划过程',
    'Engineering Reasoning': '计划依据',
    'Error Analysis': '错误分析',
    'Show': '展开',
    'Hide': '收起',
    'Generated Logic': '生成代码',
    'Usage Example': '使用示例',
    'Source Code': '源代码',
    'Copy Code': '复制代码',
    'Copy Project Summary': '复制项目总结',
    'Copy Summary': '复制总结',
    'Download Code': '下载代码',
    'Program File': '程序文件',
    'Ready to Download': '可下载',
    'Final PLC program file': '最终 PLC 程序文件',
    'Download Program': '下载程序',
    'Static Analysis Warnings': '静态分析告警',
    'Line': '行',
    'AI Provider Settings': 'AI 供应商设置',
    'Active Provider': '当前供应商',
    'Model': '模型',
    'API Keys': 'API Key',
    'Base URLs (Optional)': 'Base URL（可选）',
    'Preferences': '偏好设置',
    'Theme': '主题',
    'Default Language': '默认语言',
    'Cancel': '取消',
    'Save Changes': '保存更改',
    'System': '跟随系统',
    'Dark': '深色',
    'Light': '浅色',
    'Chinese': '中文',
    'English': '英文',
    Motion: '运动控制',
    PID: 'PID',
    Sequencing: '顺序控制',
    Motor: '电机控制',
    Process: '工艺流程',
    'SafetySystem': '安全系统',
    Safety: '安全',
    Data: '数据',
    HVAC: '暖通空调',
    Template: '模板',
    'Conveyor Control': '输送线控制',
    'PID Loop': 'PID 回路',
    'Traffic Sequence': '交通灯顺序',
    'Motor Starter': '电机启动器',
    "Control a conveyor belt with start/stop buttons, a safety e-stop, and a 10s timeout if the sensor isn't triggered.": '控制一条带启停按钮、安全急停和传感器未触发 10 秒超时保护的输送线。',
    'Create a PID temperature control loop for a heating element with anti-windup.': '为带抗积分饱和的加热元件创建 PID 温度控制回路。',
    'Implement a traffic light sequence for a 4-way intersection using timers and states.': '使用定时器和状态机实现一个四向路口交通灯顺序。',
    'Write a function block to handle a motor starter with overload protection and feedback monitoring.': '编写一个带过载保护和反馈监控的电机启动器功能块。',
    'AI 定义 PLC 编程的未来': 'AI 定义 PLC 编程的未来',
    'AI can make mistakes. Please verify generated logic before deploying to production hardware.': 'AI 可能出错，请在部署到生产硬件前验证生成逻辑。',
    'Expert Collection': '专家场景',
    'More': '更多',
    'Search Web': '联网搜索',
    'Upload File': '上传文件',
    'Drop files to upload': '拖拽文件到此处上传',
    'Files': '文件',
    'Uploading & parsing...': '上传并解析中...',
    'Maximum {n} files allowed': '最多支持 {n} 个文件',
    'Please analyze the uploaded files and provide suggestions within the RealPLC context.': '请分析上传的文件，并结合 RealPLC PLC 工程上下文给出建议。',
    'End Conversation': '停止生成',
    'Tank Control': '液位罐控制',
    'Batch Process': '批处理流程',
    'Alarm Manager': '报警管理',
    'Data Logger': '数据记录',
    'Packaging Line': '包装线',
    'HVAC Control': '暖通空调控制',
    // Template starterPrompt translations (API version)
    'Design a conveyor control program with start/stop, jam detection, emergency stop, and fault recovery.': '设计传送带控制程序，包含启停、堵料检测、急停和故障恢复。',
    'Create a PID temperature control loop with manual/auto mode, tuning parameters, limits, and alarm handling.': '创建 PID 温度控制回路，包含手动/自动模式、整定参数、限值和报警处理。',
    'Generate a traffic light sequence with safe interlocks, timers, pedestrian request handling, and fault fallback.': '生成交通灯顺序，包含安全联锁、定时器、行人请求处理和故障回退。',
    'Build a motor starter with overload protection, run feedback timeout, reset behavior, and start/stop pushbuttons.': '构建电机启动器，包含过载保护、运行反馈超时、复位逻辑和启停按钮。',
    'Generate tank level control with fill/drain logic, high and low level protection, manual/auto mode, and alarms.': '生成水箱液位控制，包含灌排逻辑、高低位保护、手动/自动模式和报警。',
    'Create a batch process sequence with step control, completion conditions, timeout supervision, and safe recovery.': '创建批次处理顺序，包含步骤控制、完成条件、超时监控和安全恢复。',
    'Design an alarm manager with latch, horn, acknowledgment, reset, active state, and operator visibility.': '设计报警管理器，包含锁存、喇叭、确认、复位、激活状态和操作员可见性。',
    'Generate a data logging routine for process values, event timestamps, alarm history, and communication-safe buffering.': '生成数据记录例程，用于过程值、时间戳、报警历史和通信安全缓冲。',
    'Build a packaging line sequence with station interlocks, reject logic, timeout supervision, and safe restart handling.': '构建包装线顺序，包含工位联锁、剔除逻辑、超时监控和安全重启处理。',
    'Generate HVAC control with heat/cool arbitration, fan coordination, manual/auto modes, and safety alarms.': '生成暖通空调控制，包含冷热仲裁、风机协调、手动/自动模式和安全报警。',
    'Control a liquid tank with inlet/outlet valves, level sensors, and start/stop buttons.': '控制一套带进出阀、液位传感器和启停按钮的液位罐。',
    'Control a multi-step batch recipe process including ingredient dosing, mixing, and discharging.': '控制一套包含投料、混合和排料的多步骤批处理流程。',
    'Implement an alarm handler with priority levels, acknowledgment, auto-reset, and horn control.': '实现带优先级、确认、自动复位和蜂鸣器控制的报警处理器。',
    'Create a circular data logger to record analog values and timestamps into a buffer.': '创建循环数据记录器，将模拟量和时间戳写入缓冲区。',
    'Automate a packaging line including filling, sealing, labeling, and rejection functions.': '自动化一条包含灌装、封口、贴标和剔除功能的包装线。',
    'Control a heating/cooling HVAC system with zone temperatures, fan speeds, and scheduling.': '控制一套带分区温度、风速和时序计划的暖通空调系统。',
    'Less': '收起',
    'Export': '导出',
    'Select Format': '选择格式',
    'PNG Image': 'PNG 图片',
    'JPG Image': 'JPG 图片',
    'SVG Vector': 'SVG 矢量',
    'Logic Flow Visualization': '逻辑流程可视化',
    'Fullscreen View': '全屏查看',
    'Close': '关闭',
    'User Profile': '用户资料',
    'Account and Profile': '账号与资料',
    'Personal display': '个人展示资料',
    'Used for your workspace avatar and sidebar identity.': '用于工作区头像和侧边栏身份展示。',
    'Used for your avatar, sidebar, and collaboration identity.': '用于头像、侧边栏和协作场景中的展示。',
    'Account security': '账号安全',
    Account: '账号',
    'Authenticated User': '已登录用户',
    'Signed in as': '当前登录账号',
    'Use account security to update your username, profile, or password.': '可在账户安全中修改用户名、资料或密码。',
    'Manage account details': '修改账户资料',
    'Current Password': '当前密码',
    'New Password': '新密码',
    'Confirm New Password': '确认新密码',
    'Update Password': '更新密码',
    'Failed to update password': '密码更新失败',
    'Password updated. Please sign in with the new password next time.': '密码已更新。请在下次登录时使用新密码。',
    'Use at least 8 characters. Your current password is verified before RealPLC updates Keycloak.': '请使用至少8个字符。更新账号密码前会先验证当前密码。',
    'Use at least 8 characters. Your current password is verified before updating the account password.': '请使用至少8个字符。更新账号密码前会先验证当前密码。',
    'Change password': '修改密码',
    'Administrators can reset passwords, but cannot view passwords after users change them.': '管理员可以重置密码，但用户修改后管理员无法查看密码。',
    'Sign out now': '立即退出登录',
    'Name': '姓名',
    'Display Name': '呢称',
    'Display name placeholder': '请输入呢称',
    'Username is required': '请输入用户名。',
    'Username cannot contain Chinese characters': '用户名不能包含中文字符。',
    'Username rule hint': '用户名需为 3-255 位，只能使用字母、数字、点号、下划线、@、加号或连字符。',
    'Phone rule hint': '手机号只能包含数字、空格、+、- 或括号。',
    'Email': '邮箱',
    'Phone': '手机号',
    'Your name': '你的姓名',
    'Today': '今天',
    'Yesterday': '昨天',
    '7 Days': '7 天内',
    '30 Days': '30 天内',
    'Starred': '星标对话',
    'Star': '收藏',
    'Unstar': '取消收藏',
    'Rename': '重命名',
    'Delete': '删除',
    'Help': '帮助',
    'Contact': '联系',
    'Sponsor': '支持我们',
    'Thinking...': '思考中...',
    'Thinking in progress': '思考中...',
    'Thinking completed': '思考完成',
    'Thinking summary in progress': '正在结合当前输入、上下文和可用项目资料理解需求。',
    'Thinking summary analysis ready': '已完成当前输入、上下文和附件的需求理解。',
    'Thinking summary clarification ready': '已完成需求理解，下面准备了需要确认的关键项。',
    'Thinking summary plan ready': '需求理解已完成，工程计划已准备就绪。',
    'Requirement handoff after thinking': '我已经理解了你的需求。接下来先把关键信号、控制逻辑和需要确认的点整理出来。',
    'Plan generation ready transition': '需求已确认，开始生成工程计划。',
    'Thinking summary non engineering': '本轮不是 PLC 工程任务，因此不进入需求分析。',
    'Thinking progress requirement analysis': '需求分析正在流式整理中：正在识别控制对象、I/O、联锁、安全约束和待确认点。',
    'Thinking progress waiting confirmations': '确认项已经生成。需要先完成必填人工确认，再进入工程推理和计划生成。',
    'Thinking progress confirmations ready': '必填确认项已准备完成，下一步进入工程推理。',
    'Thinking progress streaming': '正在分析需求并准备工程化流程...',
    'Thinking step intent': '判断本轮意图',
    'Thinking step context': '检查是否关联上一轮需求',
    'Thinking step sources': '读取当前输入、上下文和附件',
    'Thinking step engineering route': '判断是否进入 PLC 工程流程',
    'Thinking step output shape': '判断需要 PRG、FB、FC，还是普通回复',
    'Thinking step io safety': '识别控制对象、I/O、联锁和异常处理',
    'Thinking step confirmations': '筛选会影响代码行为的确认项',
    'Thinking step non engineering': '已判断为非 PLC 工程对话',
    'Requirement analysis transition clarify': '下面只确认会影响控制逻辑、代码结构或安全策略的关键项。',
    'Reasoning': '推理过程',
    'Running validation...': '正在校验...',
    'Attempting auto-repair...': '正在尝试自动修复...',
    'Logic Flow': '逻辑流程',
    'Edit in draw.io': '在 draw.io 中编辑',
    'Opening draw.io...': '正在打开 draw.io...',
    'draw.io editor ready': 'draw.io 编辑器已就绪',
    'draw.io diagram saved': 'draw.io 图已保存',
    'draw.io diagram autosaved': 'draw.io 图已自动保存',
    'Download draw.io XML': '下载 draw.io XML',
    'Download Mermaid Source': '下载 Mermaid 源码',
    'Download .drawio': '下载 .drawio',
    'Download Mermaid': '下载 Mermaid',
    'Code': '代码',
    'Diagram': '图示',
    'Show Raw Code': '查看原始代码',
    'Show Diagram': '查看图示',
    'Zoom Out': '缩小',
    'Reset Zoom': '重置缩放',
    'Zoom In': '放大',
    'Fullscreen': '全屏',
    'Chart auto-corrected ({n} changes)': '此图表已自动修正（{n}处）',
    'Chart degraded ({n} changes)': '此图表已降级渲染（{n}处）',
    'View Details': '查看修改',
    'Hide Details': '隐藏修改',
    'Corrections': '修正列表',
    'Final error': '最终错误',
    'Strict Safety Check': '严格安全校验',
    'Safety Verification Report': '安全校验报告',
    'Raw Code': '原始代码',
    'Framework Validation': '框架校验',
    'Quality Check': '质量校验',
    'Issues': '个问题',
    'No issues': '无问题',
    'Risk': '风险',
    'After repair': '修复后',
    'Issue': '问题',
    low: '低',
    medium: '中',
    high: '高',
    critical: '严重',
    'Repair History': '修复历史',
    'Structured Requirement': '结构化需求',
    'Generated Test Cases': '生成的测试用例',
    'Task Type': '任务类型',
    'Strategy': '策略',
    'Scenario Template': '场景模板',
    'Modes': '模式',
    'IO Points': 'IO 点位',
    'Signal Name': '信号名称',
    'Address': '硬件地址',
    'Variable Name': '变量名',
    'Role': '角色',

    'Key Conditions': '关键条件',
    'Missing Information': '缺失信息',
    'Preconditions': '前置条件',
    'Steps': '步骤',
    'Expected': '预期结果',
    'Syntax': '语法',
    'Semantic': '语义',
    'Vendor': '厂商',
    'Test': '测试',
    'Pass': '通过',
    'Check': '检查',
    'Template Rule': '模板规则',
    'Attempt': '尝试',
    normal: '正常',
    boundary: '边界',
    fault: '故障',
    recovery: '恢复',
    none: '无',
    tracked: '已跟踪',
    applied: '已应用',
    pending: '待处理',
    skipped: '已跳过',
    failed: '失败',
    'Execute Plan': '执行计划',
    'Subtasks': '子任务',
    'Share of the analysed safety constraints that a safety subtask covers. It does not judge whether the safety logic is right.':
      '分析列出的安全约束里，被 safety 类子任务覆盖的比例。它不判断安全逻辑对不对。',
    'Blocking': '阻断',
    'Advisory': '提醒',
    'I have read the {count} blocking self-check finding(s) above and still want to execute the plan':
      '我已了解上述 {count} 条阻断性自检问题，仍要执行',
    'Edit': '编辑',
    'Feedback': '反馈',
    'Skip': '跳过',
    'Re-plan': '重新规划',
    'Submit Changes': '提交修改',
    'Submitted': '已提交',
    'Required': '必填',
    'Auto-confirmed': '自动确认',
    'Describe plan issues, AI will re-plan...': '描述 plan 的问题，AI 会重新规划...',
    'Regenerate subtask': '重新生成子任务',
    'Optional constraints...': '可选约束...',
    'Plan review submit failed': '计划审批提交失败',
    'Plan review request id is missing, please retry.': '计划审批 requestId 缺失，请重试。',
    'Details': '详情',
    'Subtask Count ({count})': '子任务个数（{count}）',
    'Dependencies (comma-separated)': '依赖（逗号分隔）',
    'Acceptance Criteria': '验收标准',
    'Complexity': '复杂度',
    'Title': '标题',
    'Description': '描述',
    'complexity.simple': '简单',
    'complexity.moderate': '中等',
    'complexity.complex': '复杂',
    'clarify.other': '其他...',
    'clarify.pleaseSelect': '请选择',
    'clarify.confirmAndSend': '确认并发送',
    'clarify.promptHint': '或直接在下方输入回复',
    'clarify.confirmTitle': '生成前需要确认以下信息',
    'clarify.responseIntro': '关于之前的澄清问题：',
    'clarify.notSelected': '(未选择)',
    'clarify.customValue': '(自定义)',
    'clarify.customPlaceholder': '输入自定义选项...',
    'clarify.separator': '、',
    'clarify.defaultValue': '默认建议',
    'clarify.multiSelect': '可多选',
    'clarify.review.safety': '安全决策',
    'clarify.review.keyDecision': '关键决策',
    'clarify.review.noDefault': '待作答',
    'clarify.collapsedGroup': '另有 {count} 项已有推荐值，展开查看',
    'clarify.blockedByUnanswered': '还有 {count} 项待作答，确认后才能发送',
    'clarify.subject.estop': '急停',
    'clarify.subject.timeout': '超时保护',
    'clarify.subjectCount': '{count} 个问题',
    'clarify.statedInRequirement': '需求中已提及',
    'clarify.autoSubmitCountdown': '全部选项已自动确认，将在 {seconds} 秒后提交',
    'Warning': '警告',
    'Degraded': '降级',
    SafetyCategory: '安全',
    'Generating code...': '正在生成代码...',
    'Finalizing...': '正在收尾...',
    'DocumentTitle': 'RealPLC - AI 定义 PLC 编程的未来',
    'Admin Console': '管理后台',
    'Platform Admin': '平台管理员',
    'Organization Admin': '组织管理员',
    Avatar: '头像',
    'Advanced Prompting': '高级提示词',
    'Custom instructions enabled': '已启用自定义指令',
    'Using default instructions': '使用默认指令',
    'Account Center': '账号中心',
    'Account Profile': '账号资料',
    'Account Security': '账号安全',
    'Current sign-in': '当前登录',
    'Login Account': '登录账号',
    'Personal Profile': '个人资料',
    'Password and sign-in': '密码与登录',
    'Not set': '未设置',
    'Loading account profile': '正在加载账号资料',
    'Failed to load account profile': '账号资料加载失败',
    'Failed to update account profile': '账号资料更新失败',
    'Account profile updated': '账号资料已更新',
    'Username': '用户名',
    'First Name': '名字',
    'Last Name': '姓氏',
    'Save Account Profile': '保存账号资料',
    'Sign out of your current account from this device.': '从当前设备退出这个账号。',
    'Custom System Instructions (Naming conventions, safety rules, etc.)': '自定义系统指令（命名规范、安全规则等）',
    'Example custom instructions': '例如：变量统一使用匈牙利命名，或安全回路正常时 NC 急停应为 TRUE',
    'These instructions are prepended to every AI generation request to influence naming, logic, or coding style.': '这些指令会附加到每次 AI 生成请求前，用来影响命名、逻辑和代码风格。',
    'Automatic Repair Completed': '自动修复已完成',
    'Automatic Repair Completed With Warnings': '自动修复已完成（有警告）',
    'Automatic Repair Finished With Issues': '自动修复已完成（存在遗留问题）',
    'Automatic Repair Needs Review': '自动修复需复核',
    'Automatic Repair In Progress': '自动修复进行中',
    'Automatic Repair Skipped': '自动修复已跳过',
    'Total': '总计',
    'The main code block has been updated with the repaired code.': '主代码块已使用修复后的代码更新。',
    'Auto Repaired': '已自动修复',
    'Auto Repairing': '修复中',
    'Viewer': '查看者',
    'Classifying request...': '正在分类请求...',
    'Planning...': '正在规划...',
    'Analyzing requirements...': '分析需求中...',
    'Decomposing tasks...': '拆解任务中...',
    'Assessing risks...': '评估风险中...',
    'Self-checking plan quality...': '自检计划质量中...',
    'Plan completed': '计划完成',
    'Plan generation did not produce results': '计划生成未产生结果',
    'subtasks': '子任务',
    'Awaiting Confirmation': '等待确认',
    'Validating': '验证中',
    'Plan timed out, click to continue execution': '审批时间已过，计划仍可执行',
    'Continue Execution': '继续执行',
    'Approve Plan': '批准计划',
    'Reject Plan': '拒绝计划',
    'Modify Plan': '修改计划',
    'Plan approved': '计划已批准',
    'Plan rejected, re-planning...': '计划已拒绝，重新规划中...',
    'Re-planning': '重新规划',
    'Subtask Re-planning': '子任务重新规划',
    'Cached Plan Review': '缓存计划审视',
    'Re-planning in progress....': '重新规划中....',
    'Plan review timeout, auto-approved': '审批超时，必须人工确认',
    'Auto-approved': '已自动批准',
    'Manually approved': '已手动批准',
    'Auto-approving in {seconds}s': '{seconds}秒后自动批准',
    'Pause auto-approval': '暂停自动批准',
    'Resume auto-approval': '继续自动批准',
    'Plan auto-approval': '计划自动批准',
    'Countdown seconds': '倒计时秒数',
    'Enabled': '已开启',
    'Disabled': '已关闭',
    'Architecture': '架构',
    'Variable Strategy': '变量策略',
    'Safety Approach': '安全策略',
    'Risks': '风险',
    'Coverage': '覆盖率',
    'Safety Coverage': '安全覆盖率',
    'Self Check': '自检',
    'Dependencies': '依赖',
    'Execution': '执行',
    'Workspace': '工作区',
    'Engineering Workspace': '工程工作区',
    'Expand': '展开',
    'Context': '上下文',
    'Requirement': '需求',
    'IO': 'IO',
    'Plan': '计划',
    'View details': '查看详情',
    'Requirement IO': '需求IO',
    'Code IO': '代码IO',
    'I/O Points': 'I/O 点位',
    'Signals in Code': '代码中的信号',
    'IO count mismatch': 'IO数量不匹配',
    'The generated code may use a different number of signals than specified.': '生成的代码使用的信号数量可能与指定的不同。',
    'Model Name': '模型名称',
    'Model Provider Name': '模型供应商名称',
    'Model Name (OpenAI format)': '模型名称（OpenAI 格式）',
    'OpenAI-compatible provider': 'OpenAI 兼容供应商',
    'Use the exact model name accepted by the provider API.': '填写供应商 API 接受的模型名称，例如 gpt-4o-mini、deepseek-chat 或 qwen-plus。',
    'Base URL': 'Base URL',
    'Custom Base URL': '自定义 Base URL',
    'Test Connection': '测试连接',
    'Testing connection...': '正在测试连接...',
    'Connection succeeded': '连接成功',
    'Connection failed': '连接失败',
    'Enter a model name before saving.': '保存前请先填写模型名称。',
    'Enter a Base URL before saving.': '保存前请先填写 Base URL。',
    'Enter a model name before testing.': '测试前请先填写模型名称。',
    'Enter a Base URL before testing.': '测试前请先填写 Base URL。',
    'Keys are stored locally in your browser.': '密钥只会保存在当前浏览器本地。',
    'Custom Model': '自定义模型',
    'Custom Provider': '自定义供应商',
    'Custom model connections': '自定义模型连接',
    'Custom provider connections': '自定义供应商连接',
    'Models in this provider': '此供应商的模型',
    'New Model': '新模型',
    'No models added yet.': '尚未添加模型。',
    'Add Model': '添加模型',
    'Add a custom provider before saving.': '保存前请先添加自定义供应商。',
    'Optional': '可选',
    'Fetch Models': '获取模型',
    'Fetching models...': '正在获取模型...',
    'No new models found.': '未找到新模型。',
    'Model discovery failed': '模型发现失败',
    'Discovered Models': '发现的模型',
    'Add All': '添加全部',
    'Enable': '启用',
    'Disable': '禁用',
    'Enter a Base URL before fetching models.': '获取模型前请先填写 Base URL。',
    'gpt-4o-mini, deepseek-chat, qwen-plus': 'gpt-4o-mini、deepseek-chat、qwen-plus',
    'Add': '添加',
    'Remove': '移除',
    'Scanning...': '正在扫描文件安全性',
    'Converting...': '正在兼容转换...',
    'Parsing...': '正在解析文档...',
    'Ready': '就绪',
    'Parsed (compat)': '已通过兼容模式解析',
    'Text only (compat)': '已提取纯文本，版式可能不同',
    'Parse failed': '解析失败',
    'Conversion failed': '转换失败',
    'File exceeds {n}MB': '文件超过 {n}MB 限制',
    'Unsupported type': '不支持的文件类型',
    'Only {n} files allowed': '最多 {n} 个文件',
    'Processing...': '处理中，暂不能发送',
    'Agent': '代理',
    'RealPLC Agent': 'RealPLC Agent',
    'RealPLC Agent Settings': 'RealPLC Agent 设置',
    'Connect RealPLC Agent': '连接 RealPLC Agent',
    'RealPLC Agent Ready': 'RealPLC Agent 已就绪',
    'Fix RealPLC Agent': '修复 RealPLC Agent',
    'RealPLC Agent enabled': 'RealPLC Agent 已启用',
    'RealPLC Agent disabled': 'RealPLC Agent 已禁用',
    'Enable local RealPLC Agent pairing, connector environment checks, and SCL sandbox validation from the chat workspace.': '启用本地 RealPLC Agent 配对、连接器环境检查，以及聊天工作区中的 SCL 沙盒验证。',
    'Platform': '平台',
    'Sandbox validation': '沙盒验证',
    'Future Agents': '未来 Agent',
    'CODESYS and other industrial agents will use this Agent section when their connectors are added.': '后续添加连接器后，CODESYS 和其他工业 Agent 将使用这个 Agent 分区。',
    'Failed to load RealPLC Agent status': '加载 RealPLC Agent 状态失败',
    'Pairing code created. Enter it in the local RealPLC Agent.': '配对码已创建，请在本地 RealPLC Agent 中输入。',
    'Failed to create pairing code': '创建配对码失败',
    'Configuration successful. SCL closed-loop validation is available.': '配置成功，SCL 闭环验证已可用。',
    'TIA Portal connector environment check failed': 'TIA Portal 连接器环境检查失败',
    'Pairing code copied.': '配对码已复制。',
    'Pair, detect, and prepare Siemens SCL validation.': '配对、检测并准备 Siemens SCL 验证。',
    'Pair local RealPLC Agent': '配对本地 RealPLC Agent',
    'Create a short-lived code and enter it in the local CloudBridge agent.': '创建一个短期有效的配对码，并在本地 CloudBridge Agent 中输入。',
    'Create Pairing Code': '创建配对码',
    'Pairing Code': '配对码',
    'Expires': '过期时间',
    'Agent Status': 'Agent 状态',
    'Status is scoped to the current organization and project when available.': '状态会尽量限定在当前组织和项目范围内。',
    'Status': '状态',
    'Connection': '连接',
    'Retry': '重试',
    'Refresh': '刷新',
    'Agent is connected. Run the environment check to finish setup.': 'Agent 已连接，请运行环境检查完成设置。',
    'Waiting for local RealPLC Agent connection.': '正在等待本地 RealPLC Agent 连接。',
    'Environment Check': '环境检查',
    'Runs a read-only snapshot check before SCL validation is enabled.': '启用 SCL 验证前，会先运行只读快照检查。',
    'Check Environment': '检查环境',
    'Unknown': '未知',
    // Pipeline 阶段名称
    'Thinking': '思考',
    'Fast': '快速',
    'Generating answer': '正在生成回答',
    'Request Routing': '请求路由',
    'Requirement Clarification': '需求澄清',
    'Requirement Confirmation': '需求确认',
    'Human Confirmation': '人工确认',
    'Plan Review': '计划审批',
    'Code Generation': '生成代码',
    'Validation': '校验',
    'Repair': '修复',
    // Pipeline 状态文案：使用工程阶段语义，不再所有阶段都显示“已通过”
    'Completed': '已完成',
    'Confirmed': '已确认',
    'Validation Passed': '校验通过',
    'Confirmation Prepared': '确认项已生成',
    'Needs Input': '待人工确认',
    'Needs input': '待人工确认',
    'Needs Approval': '待审批',
    'Needs approval': '待审批',
    'Needs review': '待审查',
    'Running': '执行中',
    'Pending': '等待中',
    'Passed': '已通过',
    'Skipped': '已跳过',
    'Failed': '失败',
    'Repaired': '已修复',
    'Queued...': '排队中...',
    'Starting worker...': '正在启动执行器...',
    'Calling planning model...': '正在调用规划模型...',
    'Waiting for plan review data...': '正在等待计划审批数据...',
    'Submitting...': '提交中...',
    'Plan updated': '计划已更新',
    'Plan Reviews': '计划审批记录',
    'Auto-approval paused': '自动审批已暂停',
    // Workspace / artifact 区域
    'Messages': '消息',
    'Artifacts': '工件',
    'Artifact Ledger': '工件台账',
    'Version history': '版本历史',
    'Requirement Artifacts': '需求工件',
    'Active Topic': '当前主题',
    'Goal': '目标',
    'Confirmed Facts': '已确认事实',
    'Decisions': '决策',
    'Open Questions': '待确认问题',
    'Engineering context is empty': '工程上下文为空',
    'No context available': '暂无上下文',
    'No requirement artifacts yet': '暂无需求工件',
    'No I/O data yet': '暂无 I/O 数据',
    'No plan artifacts yet': '暂无计划工件',
    'No code artifacts yet': '暂无代码工件',
    'No validation results yet': '暂无校验结果',
    'No files available': '暂无文件',
    'Workbook': '工作簿',
    'Sync to workbook': '同步到工作簿',
    'Open engineering workspace': '打开工程工作区',
    'lines': '行',
    'files': '文件',
    'issue': '问题',
    'artifacts': '个工件',
    'context active': '上下文活跃',
    // 空状态 / 异常状态
    'Pipeline analysis not available. The model may not have completed the thinking process.': '暂无 Pipeline 分析结果，模型可能尚未完成思考过程。',
    'No structured output received. The model may not have followed the expected format.': '未收到结构化输出，模型可能没有按预期格式返回。',
    'Try using a model with thinking capabilities or enable strict safety check.': '可以尝试使用支持思考能力的模型，或开启严格安全校验。',
    'Try rephrasing your request or using a different model.': '可以换一种描述方式，或切换其他模型重试。',
    'Generation failed': '生成失败',
    'Assertions': '断言',
    'Some assertions failed': '部分断言未通过',
    // 修复 / 下载
    'Auto Repair': '自动修复',
    'Repair candidate was kept for review. The main code block was not replaced.': '修复候选结果已保留供复核，主代码块未被替换。',
    'Repaired Logic': '修复后代码',
    'Repair Attempts': '修复尝试',
    'Final Validation': '最终校验',
    'Regenerated Plan': '重新生成计划',
    'Repair candidate': '修复候选版本',
    'has been rendered separately below': '已在下方单独渲染',
    'for comparison. The original code area is retained.': '用于对比，原始代码区域已保留。',
    'is rendered separately for review.': '已单独渲染供复核。',
    'remains the active/default version.': '仍是当前默认版本。',
    'Rendering diagram...': '正在渲染图表...',
    'Preparing diagram...': '正在准备图表...',
    'The chart will appear here as soon as Mermaid finishes rendering.': 'Mermaid 渲染完成后会显示在这里。',
    'Message actions': '消息操作',
    'Copy summary': '复制总结',
    'Copy failed': '复制失败',
    'Engineering Results': '工程结果',
    'The target platform is fixed while a turn is running.': '本轮生成进行中，目标平台已锁定。',
    'The programming language is fixed while a turn is running.': '本轮生成进行中，编程语言已锁定。',
    'Ladder Diagram (LAD)': '梯形图 (LAD)',
    'Ladder diagram': '梯形图',
    'Source text': '文本',
    'Ladder network': '程序段',
    'Empty network': '空程序段',
    'Written in SCL; see the text view': '以 SCL 编写，见文本页',
    'Execution Record': '执行记录',
    'Overview': '概览',
    'Solution': '方案',
    'Engineering Artifacts': '产物',
    'Verification': '验证',
    'Engineering result sections': '工程结果分区',
    'Deliverable files': '交付文件',
    'Code versions': '代码版本',
    'Related records': '关联记录',
    'Deliverable files will appear here after generation completes.': '尚未生成可交付文件；生成完成后会显示在这里。',
    'result records': '项记录',
    'Control Logic': '控制逻辑',
    'Close Engineering Results': '关闭工程结果',
    'Resize Engineering Results': '调整工程结果区域宽度',
    'Confirmed decisions': '已确认工程约束',
    'items': '项',
    'Preview': '预览',
    'Click to preview': '点击预览',
    'Close preview': '关闭预览',
    'Maximize panel': '展开面板',
    'Restore width': '还原宽度',
    'Show all': '显示全部',
    'Open in panel': '在面板中打开',
    'Copy': '复制',
    'Copied': '已复制',
    'Download': '下载',
    'Show less': '收起',
    'Search chats': '搜索会话',
    'Search results': '搜索结果',
    'No matching chats': '没有匹配的会话',
    'Save and regenerate': '保存并重新生成',
    'Changes': '本次改动',
    'Full program': '完整程序',
    'Previous version': '上一个版本',
    'Next version': '下一个版本',
    'Retry with another model': '换一个模型重试',
    'Continue generating': '继续生成',
    'Continues as a new turn using what was already written.': '会带着已写出的内容作为新一轮继续，不是断点续传。',
    'Continue from where the previous answer stopped.': '请接着上一条回答被中断的位置继续。',
    'Export deliverable': '导出交付包',
    'Export failed': '导出失败',
    'Waiting for your confirmation': '等你确认需求',
    'Waiting for your plan approval': '等你确认控制方案',
    'Install and update': '安装与更新',
    'Checking the latest RealPLC Agent release...': '正在检查最新的 RealPLC Agent 版本…',
    'This Agent version is no longer supported. Update before continuing.': '当前 Agent 版本已不再受支持，请更新后继续。',
    'A newer RealPLC Agent version is available.': '发现新版 RealPLC Agent。',
    'The installed RealPLC Agent is up to date.': '当前安装的 RealPLC Agent 已是最新版本。',
    'Download the Windows x64 installer, then return here to pair the Agent.': '下载 Windows x64 安装包，安装后返回此处配对 Agent。',
    'The download will appear after a release source is configured.': '配置发布来源后，这里将显示下载入口。',
    'Installed version': '已安装版本',
    'Latest version': '最新版本',
    'Update required': '必须更新',
    'Update to': '更新到',
    'Download and install': '下载并安装',
    'Checking release': '检查版本中',
    'Download unavailable': '暂不可下载',
    'View release notes': '查看更新说明',
    'Failed to load RealPLC Agent release information': '加载 RealPLC Agent 发布信息失败',
    'Install or connect RealPLC Agent': '安装或连接 RealPLC Agent',
    // Its own Chinese, so the English entry for this Chinese key is not what the Chinese UI falls back to.
    '启动本地 RealPLC Agent 后可进行 **TIA Portal 编译验证**。\n\nAgent 上线后不会自动开始验证，就绪后在这里点「立即验证」。':
      '启动本地 RealPLC Agent 后可进行 **TIA Portal 编译验证**。\n\nAgent 上线后不会自动开始验证，就绪后在这里点「立即验证」。',
    'Agent 离线 · 上线后这里会自动更新': 'Agent 离线 · 上线后这里会自动更新',
    'Loading conversation...': '正在加载对话…',
    'Could not load this conversation.': '无法加载此对话。',
    'Choose a template to preview and edit it before sending.': '选择模板后会先填入输入框，你可以检查和修改后再发送。',
    'Complete the required clarification above before continuing.': '请先完成上方必填的需求确认，再继续对话。',
    'Complete the clarification above to continue...': '请先完成上方需求确认…',
    'Answer the confirmation above, or write your reply here...': '在上方选择，或直接在这里写回复…',
    'Saved as the default engineering context for future conversations.': '修改会自动保存，并作为后续对话的默认工程上下文。',
    'Reset defaults': '恢复默认',
    'Add files or configure tools': '添加文件或配置工具',
    'Stop generation': '停止生成',
    'Send message': '发送消息',
    'Press Ctrl or Command + Enter to send. Enter inserts a new line. Esc closes the editor.': '按 Ctrl/Command + Enter 发送，Enter 换行，Esc 关闭编辑器。',
    // Agent connection status and connector controls shown on the composer chips,
    // the Settings agent panel, and the connector dialogs. English falls back to the key.
    'Online': '在线',
    'Offline': '离线',
    'Detecting': '检测中',
    'Setup': '待配置',
    // The TIA button's readiness words are the readiness table's
    // (TIA_READINESS_TEXT, via readTiaReadiness), not keys here.
    'Detecting TIA Agent': '正在检测 TIA Agent',
    'TIA Agent status unavailable · showing last known state': 'TIA Agent 状态不可用 · 显示最近一次已知状态',
    'CODESYS Agent Online · Ready': 'CODESYS Agent 在线 · 就绪',
    'CODESYS Agent Online · setup required': 'CODESYS Agent 在线 · 需要配置',
    'CODESYS Agent Offline': 'CODESYS Agent 离线',
    'Toggle TIA Agent': '切换 TIA Agent',
    'Toggle RealPLC CODESYS Agent': '切换 CODESYS Agent',
    // Keys t() was called with that had no Chinese entry until 2026-09-17, so
    // the Chinese UI showed their English text (I-005). i18n.coverage.test.ts
    // fails on the next one.
    'Latest': '最新',
    'Configure engineering context': '配置工程上下文',
    'Fast uses the low-latency automatic model pool.': '快速模式使用低延迟的自动模型池。',
    'Fast keeps the selected model and uses lower-latency pipeline settings.': '快速模式沿用所选模型，并采用更低延迟的流水线设置。',
    'Thinking uses the deep automatic model pool.': '思考模式使用深度自动模型池。',
    'Thinking keeps the selected model and uses deeper planning/validation settings.': '思考模式沿用所选模型，并采用更深入的规划与校验设置。',
    'Failed to refresh file status': '刷新文件状态失败',
    'File processing timed out. Please retry upload.': '文件处理超时，请重新上传。',
    'Failed to load image preview': '加载图片预览失败',
    'Please wait for file processing to finish.': '请等待文件处理完成。',
    'A file could not be read. Remove it to send.': '有文件未能读取，请移除后再发送。',
    // The server's sentences for a file that was not read, or not all of it
    // (UPLOAD_READ_MESSAGES in @realplc/types). The keys must stay those strings.
    'No readable text was found in this file.': '没有从这个文件里读到文字。',
    'This PDF is a scan, so its text cannot be read. Upload the original Word or Excel file, or type the key content.': '这份 PDF 是扫描件，读不到文字。请上传原始的 Word 或 Excel 文件，或把关键内容直接打出来。',
    'The text of this PDF could not be decoded. Upload the original Word or Excel file, or paste the text.': '这份 PDF 的文字无法解码。请上传原始的 Word 或 Excel 文件，或直接粘贴文字。',
    'Only the first 200 pages of this PDF were read.': '这份 PDF 只读取了前 200 页。',
    'This PDF took too long to read. Upload the part you need, or paste the text.': '这份 PDF 读取超时。请只上传需要的部分，或直接粘贴文字。',
    'This file is not readable text.': '这个文件不是可读的文本。',
    'The text encoding of this file was not recognised. Save it as UTF-8 and upload it again.': '没能识别这个文件的文字编码。请另存为 UTF-8 后重新上传。',
    'Old Office formats (.doc, .ppt) cannot be converted on this server. Save the file as .docx or .pptx and upload it again.': '旧版 Office 格式（.doc、.ppt）在这台服务器上无法转换。请另存为 .docx 或 .pptx 后重新上传。',
    'Old Office files (.doc, .xls, .ppt) are not read. Save the file as .docx, .xlsx or .pptx and upload it again.': '旧版 Office 文件（.doc、.xls、.ppt）读不了。请另存为 .docx、.xlsx 或 .pptx 后重新上传。',
    'Pictures inside this document were not read.': '文档里的图片没有读取。',
    'Columns after the 256th of a sheet were not read.': '工作表第 256 列之后的内容没有读取。',
    'Unsupported file type: {names}': '不支持的文件类型：{names}',
    'Remove file': '移除文件',
    'Files in this conversation: {n}': '本对话的资料 {n} 份',
    '{n} not read in full': '{n} 份未完整读取',
    'Remove from this conversation': '从本对话移除',
    'Remove this file? Later turns will no longer be given it.': '移除这份文件？之后每一轮都不再带它。',
    'uploading...': '上传中...',
    'Expand editor': '展开编辑器',
    'Understanding image...': '正在理解图片...',
    'Preparing context...': '正在准备上下文...',
    'Failed to delete uploaded file': '删除已上传文件失败',
    'Model and Routing': '模型与路由',
    'draw.io: stateDiagram support is limited, layout may differ': 'draw.io 对 stateDiagram 的支持有限，布局可能有差异',
    'Edit message': '编辑消息',
    'The model ran out of output room and the plan was cut off.': '模型输出空间用尽，方案被截断。',
    'The model did not return a plan.': '模型没有返回方案。',
    'Degraded plan': '降级方案',
    'What follows was assembled from the requirement structure and has not been reviewed by the model. Read it as a starting point, not as an approved plan.': '以下内容由需求结构自动拼装而成，未经模型审阅。请把它当作起点，而不是已批准的方案。',
    'not assessed': '未评估',
    'Not installed': '未安装',
    'Setup required': '需要配置',
    'Busy': '忙碌中',
    'Quarantined': '已隔离',
    'Waiting for user session': '等待用户会话',
    'One host connection manages independently packaged engineering connectors in the active Windows user session.': '一个主机连接在当前 Windows 用户会话中管理各自独立打包的工程连接器。',
    'Version': '版本',
    'Session': '会话',
    'Single active user': '单一活动用户',
    'Connector for Siemens TIA Portal': 'Siemens TIA Portal 连接器',
    'Independent Siemens connector for environment checks and SCL sandbox validation.': '独立的 Siemens 连接器，负责环境检查与 SCL 沙箱验证。',
    'Open TIA connector settings': '打开 TIA 连接器设置',
    'Independent CODESYS connector, ScriptEngine checks, and native ST closed-loop validation.': '独立的 CODESYS 连接器、ScriptEngine 检查与原生 ST 闭环验证。',
    'Independent sandbox': '独立沙箱',
    'Open CODESYS connector settings': '打开 CODESYS 连接器设置',
    'More actions': '更多操作',
    'Expand sidebar': '展开侧边栏',
    'TIA Connector environment check failed': 'TIA 连接器环境检查失败',
    'TIA Connector': 'TIA 连接器',
    // The TIA target is the one open project, a new one, or a choice (P2 E7),
    // and the picker above the input box always shows it.
    'The Agent connection is available in this conversation, but SCL validation needs an explicitly bound project. Open a project conversation to validate. The target is always shown on the picker above the input box, and you can change it.': '当前对话可以使用 Agent 连接，但 SCL 验证需要明确绑定的项目，请打开项目对话再验证。目标始终显示在选择条上，可以更改。',
    'Pair RealPLC Agent Core': '配对 RealPLC Agent Core',
    'Connection is organization-wide; readiness is evaluated for the current project.': '连接按组织共享，就绪状态按当前项目评估。',
    'Readiness': '就绪状态',
    'Live status unavailable; showing last known state.': '实时状态不可用，显示最近一次已知状态。',
    'Agent is Online. Select a project conversation before running validation.': 'Agent 在线。运行验证前请先选择一个项目对话。',
    'Could not load this version': '无法加载该版本',
    'Could not package the versions': '无法打包这些版本',
    'Current': '当前',
    'Rejected': '已拒绝',
    'Accepted': '已接受',
    'Compare': '对比',
    'Unchanged': '无变化',
    'Download all versions': '下载全部版本',
    'Project Context': '项目上下文',
    'Context Mode': '上下文模式',
    'Project Kernel': '项目内核',
    'Active Artifact': '当前产物',
    'Safety Rules': '安全规则',
    'Timing Rules': '时序规则',
    'Rejected Assumptions': '已否决的假设',
    'Working Memory': '工作记忆',
    'This conversation is accumulating engineering state. Consider moving it into a project.': '这个对话正在积累工程状态，建议把它移入项目。',
    'Current Intent': '当前意图',
    'Current Artifact': '当前产物',
    'Temporary Facts': '临时事实',
    'Session Preferences': '会话偏好',
    'Session Context': '会话上下文',
    'Implementation steps': '实现步骤',
    'Active': '当前',
    // Accessible names of icon-only controls (I-006).
    'Collapse sidebar': '收起侧边栏',
    'Open sidebar': '打开侧边栏',
    'Project actions': '项目操作',
    'Confirm rename': '确认重命名',
    // The composer says when it restored a draft, and offers to drop it (I-004).
    'Restored your unsent draft': '已恢复上次未发送的草稿',
    'Clear': '清除',
  }
};

Object.assign(translations.English, {
  'Agent': 'Agent',
  'RealPLC Agent': 'RealPLC Agent',
  'RealPLC Agent Settings': 'RealPLC Agent Settings',
  'Connect RealPLC Agent': 'Connect RealPLC Agent',
  'RealPLC Agent Ready': 'RealPLC Agent Ready',
  'Fix RealPLC Agent': 'Fix RealPLC Agent',
  'RealPLC Agent enabled': 'RealPLC Agent enabled',
  'RealPLC Agent disabled': 'RealPLC Agent disabled',
  'Enable local RealPLC Agent pairing, connector environment checks, and SCL sandbox validation from the chat workspace.': 'Enable local RealPLC Agent pairing, connector environment checks, and SCL sandbox validation from the chat workspace.',
  'Platform': 'Platform',
  'Sandbox validation': 'Sandbox validation',
  'Future Agents': 'Future Agents',
  'CODESYS and other industrial agents will use this Agent section when their connectors are added.': 'CODESYS and other industrial agents will use this Agent section when their connectors are added.',
  'Failed to load RealPLC Agent status': 'Failed to load RealPLC Agent status',
  'Pairing code created. Enter it in the local RealPLC Agent.': 'Pairing code created. Enter it in the local RealPLC Agent.',
  'Failed to create pairing code': 'Failed to create pairing code',
  'Configuration successful. SCL closed-loop validation is available.': 'Configuration successful. SCL closed-loop validation is available.',
  'TIA Portal connector environment check failed': 'TIA Portal connector environment check failed',
  'Pairing code copied.': 'Pairing code copied.',
  'Pair, detect, and prepare Siemens SCL validation.': 'Pair, detect, and prepare Siemens SCL validation.',
  'Pair local RealPLC Agent': 'Pair local RealPLC Agent',
  'Create a short-lived code and enter it in the local CloudBridge agent.': 'Create a short-lived code and enter it in the local CloudBridge agent.',
  'Create Pairing Code': 'Create Pairing Code',
  'Pairing Code': 'Pairing Code',
  'Expires': 'Expires',
  'Agent Status': 'Agent Status',
  'Status is scoped to the current organization and project when available.': 'Status is scoped to the current organization and project when available.',
  'Status': 'Status',
  'Connection': 'Connection',
  'Copy': 'Copy',
  'Copied': 'Copied',
  'Retry': 'Retry',
  'Refresh': 'Refresh',
  'Close': 'Close',
  'Agent is connected. Run the environment check to finish setup.': 'Agent is connected. Run the environment check to finish setup.',
  'Waiting for local RealPLC Agent connection.': 'Waiting for local RealPLC Agent connection.',
  'Environment Check': 'Environment Check',
  'Runs a read-only snapshot check before SCL validation is enabled.': 'Runs a read-only snapshot check before SCL validation is enabled.',
  'Check Environment': 'Check Environment',
  'Unknown': 'Unknown',
});



// Pipeline UI / Engineering Workspace wording补丁：
// 这些 key 由 MessageBubble、stageTimelineAdapter、EngineeringWorkspace 和 workspaceViewModel 共用。
// 统一放在覆盖层，避免正文、timeline、workspace 出现英文残留或状态词不一致。
Object.assign(translations.English, {
  'Thinking': 'Thinking',
  'Fast': 'Fast',
  'Generating answer': 'Generating answer',
  'Request Routing': 'Request Routing',
  'Requirement Clarification': 'Requirement Clarification',
  'Human Confirmation': 'Human Confirmation',
  'Code Generation': 'Code Generation',
  'Validation Passed': 'Validation passed',
  'Confirmation Prepared': 'Confirmation prepared',
  'Confirmed': 'Confirmed',
  'Completed': 'Completed',
  'Needs Approval': 'Needs approval',
  'Needs Input': 'Needs input',
  'Queued...': 'Queued...',
  'Starting worker...': 'Starting worker...',
  'Calling planning model...': 'Calling planning model...',
  'Waiting for plan review data...': 'Waiting for plan review data...',
  'Plan Reviews': 'Plan Reviews',
  'Plan updated': 'Plan updated',
  'Submitting...': 'Submitting...',
  'Auto-approval paused': 'Auto-approval paused',
  'Messages': 'Messages',
  'Artifacts': 'Artifacts',
  'Requirement Artifacts': 'Requirement Artifacts',
  'Active Topic': 'Active Topic',
  'Goal': 'Goal',
  'Confirmed Facts': 'Confirmed Facts',
  'Decisions': 'Decisions',
  'Open Questions': 'Open Questions',
  'Artifact Ledger': 'Artifact Ledger',
  'Version history': 'Version history',
  'Workbook': 'Workbook',
  'Sync to workbook': 'Sync to workbook',
  'Open engineering workspace': 'Open engineering workspace',
  'Engineering context is empty': 'Engineering context is empty',
  'No context available': 'No context available',
  'No requirement artifacts yet': 'No requirement artifacts yet',
  'No I/O data yet': 'No I/O data yet',
  'No plan artifacts yet': 'No plan artifacts yet',
  'No code artifacts yet': 'No code artifacts yet',
  'No validation results yet': 'No validation results yet',
  'No files available': 'No files available',
  'Pipeline analysis not available. The model may not have completed the thinking process.': 'Pipeline analysis not available. The model may not have completed the thinking process.',
  'No structured output received. The model may not have followed the expected format.': 'No structured output received. The model may not have followed the expected format.',
  'Try using a model with thinking capabilities or enable strict safety check.': 'Try using a model with thinking capabilities or enable strict safety check.',
  'Try rephrasing your request or using a different model.': 'Try rephrasing your request or using a different model.',
  'Generation failed': 'Generation failed',
  'Assertions': 'Assertions',
  'Some assertions failed': 'Some assertions failed',
  'Auto Repair': 'Auto Repair',
  'Repair candidate was kept for review. The main code block was not replaced.': 'Repair candidate was kept for review. The main code block was not replaced.',
  'Download': 'Download',
  'lines': 'lines',
  'files': 'files',
  'issue': 'issue',
});

Object.assign(translations.English, {
  'Regenerated Plan': 'Regenerated Plan',
  'Repaired Logic': 'Repaired Logic',
  'Repair candidate': 'Repair candidate',
  'has been rendered separately below': 'has been rendered separately below',
  'for comparison. The original code area is retained.': 'for comparison. The original code area is retained.',
  'is rendered separately for review.': 'is rendered separately for review.',
  'remains the active/default version.': 'remains the active/default version.',
  'Rendering diagram...': 'Rendering diagram...',
  'Preparing diagram...': 'Preparing diagram...',
  'The chart will appear here as soon as Mermaid finishes rendering.': 'The chart will appear here as soon as Mermaid finishes rendering.',
  'Message actions': 'Message actions',
  'Copy summary': 'Copy summary',
  'Copy failed': 'Copy failed',
  'Engineering Results': 'Engineering Results',
  'The target platform is fixed while a turn is running.': 'The target platform is fixed while a turn is running.',
  'The programming language is fixed while a turn is running.': 'The programming language is fixed while a turn is running.',
  'Ladder Diagram (LAD)': 'Ladder Diagram (LAD)',
  'Ladder diagram': 'Ladder',
  'Source text': 'Text',
  'Ladder network': 'Network',
  'Empty network': 'Empty network',
  'Written in SCL; see the text view': 'Written in SCL; see the text view',
  'Execution Record': 'Execution Record',
  'Overview': 'Overview',
  'Solution': 'Solution',
  'Engineering Artifacts': 'Artifacts',
  'Verification': 'Verification',
  'Engineering result sections': 'Engineering result sections',
  'Deliverable files': 'Deliverable files',
  'Code versions': 'Code versions',
  'Related records': 'Related records',
  'Deliverable files will appear here after generation completes.': 'Deliverable files will appear here after generation completes.',
  'result records': 'result records',
  'Control Logic': 'Control Logic',
  'Close Engineering Results': 'Close Engineering Results',
  'Resize Engineering Results': 'Resize Engineering Results',
  'Confirmed decisions': 'Confirmed decisions',
  'items': 'items',
  'Preview': 'Preview',
  'Click to preview': 'Click to preview',
  'Close preview': 'Close preview',
  'Maximize panel': 'Maximize panel',
  'Restore width': 'Restore width',
});

// Conversation rendering (unified markdown / code blocks / artifact cards)
Object.assign(translations.English, {
  'Show all': 'Show all',
  'Open in panel': 'Open in panel',
  'Search chats': 'Search chats',
  'Search results': 'Search results',
  'No matching chats': 'No matching chats',
  'Save and regenerate': 'Save and regenerate',
  'Cancel': 'Cancel',
  'Previous version': 'Previous version',
  'Next version': 'Next version',
  'Retry with another model': 'Retry with another model',
  'Continue generating': 'Continue generating',
  'Continues as a new turn using what was already written.': 'Continues as a new turn using what was already written.',
  'Continue from where the previous answer stopped.': 'Continue from where the previous answer stopped.',
  'Export deliverable': 'Export deliverable',
  'Export failed': 'Export failed',
  'Waiting for your confirmation': 'Waiting for your confirmation',
  'Waiting for your plan approval': 'Waiting for your plan approval',
});

Object.assign(translations.English, {
  'Install and update': 'Install and update',
  'Checking the latest RealPLC Agent release...': 'Checking the latest RealPLC Agent release...',
  'This Agent version is no longer supported. Update before continuing.': 'This Agent version is no longer supported. Update before continuing.',
  'A newer RealPLC Agent version is available.': 'A newer RealPLC Agent version is available.',
  'The installed RealPLC Agent is up to date.': 'The installed RealPLC Agent is up to date.',
  'Download the Windows x64 installer, then return here to pair the Agent.': 'Download the Windows x64 installer, then return here to pair the Agent.',
  'The download will appear after a release source is configured.': 'The download will appear after a release source is configured.',
  'Installed version': 'Installed version',
  'Latest version': 'Latest version',
  'Update required': 'Update required',
  'Update to': 'Update to',
  'Download and install': 'Download and install',
  'Checking release': 'Checking release',
  'Download unavailable': 'Download unavailable',
  'View release notes': 'View release notes',
  'Failed to load RealPLC Agent release information': 'Failed to load RealPLC Agent release information',
  'Install or connect RealPLC Agent': 'Install or connect RealPLC Agent',
  // The post-generation TIA card's offline text, keyed by its Chinese, which
  // the Chinese table keeps as it is.
  '启动本地 RealPLC Agent 后可进行 **TIA Portal 编译验证**。\n\nAgent 上线后不会自动开始验证，就绪后在这里点「立即验证」。':
    'Start the local RealPLC Agent to validate the program with a **TIA Portal compile**.\n\nValidation does not start by itself when the Agent comes online: once it is ready, press the validate button here.',
  // The Agent status stream says when it comes online, and a poll with a
  // growing interval stands in while the stream is down: no fixed 15 s.
  'Agent 离线 · 上线后这里会自动更新': 'Agent offline · this updates by itself once the Agent is online',
});

Object.assign(translations.English, {
  'Loading conversation...': 'Loading conversation...',
  'Could not load this conversation.': 'Could not load this conversation.',
  'Choose a template to preview and edit it before sending.': 'Choose a template to preview and edit it before sending.',
  'Complete the required clarification above before continuing.': 'Complete the required clarification above before continuing.',
  'Complete the clarification above to continue...': 'Complete the clarification above to continue...',
  'Saved as the default engineering context for future conversations.': 'Saved as the default engineering context for future conversations.',
  'Reset defaults': 'Reset defaults',
  'Add files or configure tools': 'Add files or configure tools',
  'Stop generation': 'Stop generation',
  'Send message': 'Send message',
  'Press Ctrl or Command + Enter to send. Enter inserts a new line. Esc closes the editor.': 'Press Ctrl or Command + Enter to send. Enter inserts a new line. Esc closes the editor.',
});

// Auto-detect browser language on first load
export const getBrowserLanguage = (): string => {
  if (typeof navigator === 'undefined') {
    return 'English';
  }
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  const langCode = browserLang.toLowerCase();

  // Check if browser language starts with 'zh'
  if (langCode.startsWith('zh')) {
    return 'Chinese';
  }
  return 'English';
};

const getSavedLanguage = (): string | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const saved = getItem('ui_language');
  if (saved && (saved === 'English' || saved === 'Chinese')) {
    return saved;
  }
  return null;
};

export const getLanguageSource = (): 'auto' | 'manual' => {
  if (typeof localStorage === 'undefined') {
    return 'auto';
  }
  return getItem('ui_language_source') === 'manual' ? 'manual' : 'auto';
};

export const getEffectiveLanguage = (): string => getSavedLanguage() || getBrowserLanguage();

export const getLanguagePreference = (): string => {
  if (getLanguageSource() !== 'manual') {
    return 'System';
  }
  return getSavedLanguage() || getBrowserLanguage();
};

export const persistLanguagePreference = (language: string, source: 'auto' | 'manual' = 'manual') => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (source === 'auto' || language === 'System') {
    setItem('ui_language', getBrowserLanguage());
    setItem('ui_language_source', 'auto');
    return;
  }

  setItem('ui_language', language === 'Chinese' ? 'Chinese' : 'English');
  setItem('ui_language_source', 'manual');
};

// Initialize language from localStorage or auto-detect from browser
const getInitialLanguage = (): string => {
  const saved = getSavedLanguage();
  if (saved) {
    return saved;
  }
  const detected = getBrowserLanguage();
  persistLanguagePreference(detected, 'auto');
  return detected;
};

/**
 * What a key reads as in a language: the language's own table, then English,
 * then the key itself.
 *
 * Exported so a caller can check that a key it relies on actually has an entry —
 * a missing one does not fail, it shows the lookup key, which is how a Chinese
 * turn carried the English step title 「Native compile (CODESYS)」.
 */
export function resolveTranslation(language: string, key: string): string {
  return translations[language]?.[key] || translations['English']?.[key] || key;
}

export function useTranslation() {
  const [language, setLanguage] = useState(getInitialLanguage());

useEffect(() => {
    const handleSettingsUpdated = () => {
      setLanguage(getSavedLanguage() || getBrowserLanguage());
    };

    const handleBrowserLanguageChange = () => {
      if (getLanguageSource() !== 'manual') {
        const detected = getBrowserLanguage();
        persistLanguagePreference(detected, 'auto');
        setLanguage(detected);
      }
    };

    document.documentElement.lang = language === 'Chinese' ? 'zh-CN' : 'en';

    window.addEventListener('settings_updated', handleSettingsUpdated);
    window.addEventListener('languagechange', handleBrowserLanguageChange);
    return () => {
      window.removeEventListener('settings_updated', handleSettingsUpdated);
      window.removeEventListener('languagechange', handleBrowserLanguageChange);
    };
  }, [language]);

  const t = useCallback((key: string): string => resolveTranslation(language, key), [language]);

  return { t, language };
}
